/**
 * Bank statement imports — `/finance/bank-statements`.
 *
 * The file goes up as base64 in `content_base64` rather than as multipart, so
 * the exact bytes signed for by the idempotency key are the exact bytes that
 * get checksummed. This screen encodes the picked file itself and sends the
 * encoded STRING as the mutation variable, which also keeps the logical-action
 * fingerprint over a stable value.
 *
 * Three server guarantees are surfaced verbatim rather than smoothed over: a
 * byte-identical re-upload imports nothing (`bank.statement_already_imported`),
 * overlapping days two statements share are counted once in
 * `duplicate_line_count`, and an opening balance that does not continue the
 * stored closing balance still imports but marks the file `gap_detected`. A
 * gap is a banner on this page, not a dismissible toast.
 */
import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@erp/ui";

import { DataTable, badgeCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  BANK_STATEMENT_FORMATS,
  type BankStatement,
  type BankStatementFormat,
  type BankStatementImportRequest,
  type BankStatementImportResult,
  importBankStatement,
} from "../api";
import { NoticeBanner, PageHeader, ProblemBanner, SelectField } from "../components/fields";
import {
  financeKeys,
  useBankAccountList,
  useBankImportProfiles,
  useBankStatementList,
} from "../queries";

const ALL = "__all__";
const NONE = "__none__";

/** Decoded upload cap the contract states, enforced before the request. */
const MAX_STATEMENT_BYTES = 16 * 1024 * 1024;

const STATUS_VARIANTS = {
  imported: "success",
  gap_detected: "destructive",
} as const;

/** Base64 of the exact bytes, chunked so a large file cannot blow the stack. */
export function encodeBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

/**
 * The picked file's exact bytes. `Blob.arrayBuffer` is the direct route but is
 * absent on older Safari and under jsdom, so a FileReader is the fallback —
 * both read the same bytes, which is what the server checksums.
 */
export function readFileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) resolve(new Uint8Array(reader.result));
      else reject(new TypeError("Statement file reader returned a non-binary result"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Statement file could not be read"));
    reader.readAsArrayBuffer(file);
  });
}

export interface BankStatementsPageProps {
  onOpenStatementLines?: (statementFileId: string) => void;
  canImport?: boolean;
  canReadProfiles?: boolean;
  pageLimit?: number;
}

export function BankStatementsPage({
  onOpenStatementLines,
  canImport = true,
  canReadProfiles = true,
  pageLimit,
}: BankStatementsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [bankAccountFilter, setBankAccountFilter] = useState<string>(ALL);
  const [importAccountId, setImportAccountId] = useState<string>(NONE);
  const [format, setFormat] = useState<BankStatementFormat>("csv");
  const [profileId, setProfileId] = useState<string>(NONE);
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<BankStatementImportResult | null>(null);

  const bankAccountsQuery = useBankAccountList({}, 200);
  const bankAccounts = bankAccountsQuery.rows;
  const profilesQuery = useBankImportProfiles(
    importAccountId === NONE ? null : importAccountId,
    canReadProfiles,
  );

  const filters = useMemo(
    () => (bankAccountFilter === ALL ? {} : { bank_account_id: bankAccountFilter }),
    [bankAccountFilter],
  );
  const listQuery = useBankStatementList(filters, pageLimit);

  const bankAccountName = (id: string): string => {
    const account = bankAccounts.find((candidate) => candidate.id === id);
    return account === undefined ? id : `${account.code} · ${account.name}`;
  };

  const importMutation = useIdempotentMutation<
    BankStatementImportResult,
    { bankAccountId: string; body: BankStatementImportRequest }
  >({
    mutationFn: (input, key) => importBankStatement(input.bankAccountId, input.body, key),
    onSuccess: (imported) => {
      setResult(imported);
      setFile(null);
      if (fileInputRef.current !== null) fileInputRef.current.value = "";
      void queryClient.invalidateQueries({ queryKey: financeKeys.bankStatements.all });
      void queryClient.invalidateQueries({ queryKey: financeKeys.bankStatementLines.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submitImport = async () => {
    if (importAccountId === NONE) {
      setFormError(t("finance.bankStatements.accountRequired", "Choose the bank account."));
      return;
    }
    if (file === null) {
      setFormError(t("finance.bankStatements.fileRequired", "Choose the statement file."));
      return;
    }
    if (file.size > MAX_STATEMENT_BYTES) {
      setFormError(
        t("finance.bankStatements.fileTooLarge", "A statement file may be at most 16 MiB."),
      );
      return;
    }
    setFormError(null);
    setResult(null);
    let bytes: Uint8Array;
    try {
      bytes = await readFileBytes(file);
    } catch {
      setFormError(t("finance.bankStatements.fileUnreadable", "That file could not be read."));
      return;
    }
    importMutation.mutate({
      bankAccountId: importAccountId,
      body: {
        format,
        file_name: file.name,
        content_base64: encodeBase64(bytes),
        ...(profileId === NONE ? {} : { profile_id: profileId }),
      },
    });
  };

  const columns = useMemo<ColumnDef<BankStatement>[]>(
    () => [
      {
        accessorKey: "file_name",
        id: "file_name",
        header: t("finance.bankStatements.columns.fileName", "File"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "bank_account_id",
        id: "bank_account_id",
        header: t("finance.bankStatements.columns.bankAccount", "Bank account"),
        enableSorting: false,
        cell: (ctx) => bankAccountName(ctx.getValue<string>()),
      },
      {
        id: "period",
        header: t("finance.bankStatements.columns.period", "Period"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr">
            {ctx.row.original.statement_start} → {ctx.row.original.statement_end}
          </span>
        ),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("finance.bankStatements.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            STATUS_VARIANTS,
            t(`finance.bankStatements.statusOption.${value}`, value),
          );
        },
      },
      {
        accessorKey: "imported_line_count",
        id: "imported_line_count",
        header: t("finance.bankStatements.columns.imported", "Imported"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "duplicate_line_count",
        id: "duplicate_line_count",
        header: t("finance.bankStatements.columns.duplicates", "Duplicates"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        id: "closing_balance",
        header: t("finance.bankStatements.columns.closing", "Closing balance"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.closing_balance} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- name helper closes over stable query rows
    [t, bankAccounts],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.bankStatements.title", "Bank statements")}
        description={t(
          "finance.bankStatements.subtitle",
          "Imported statement files, their continuity and what they contributed.",
        )}
      />

      {canImport ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.bankStatements.importSection", "Import a statement")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SelectField
              id="import-bank-account"
              label={t("finance.bankStatements.bankAccount", "Bank account")}
              value={importAccountId}
              onValueChange={(value) => {
                setImportAccountId(value);
                setProfileId(NONE);
              }}
              options={[
                { value: NONE, label: t("finance.common.choose", "Choose…") },
                ...bankAccounts.map((account) => ({
                  value: account.id,
                  label: `${account.code} · ${account.name}`,
                })),
              ]}
            />
            <SelectField
              id="import-format"
              label={t("finance.bankStatements.format", "Format")}
              value={format}
              onValueChange={(value) => setFormat(value as BankStatementFormat)}
              options={BANK_STATEMENT_FORMATS.map((value) => ({
                value,
                label: t(`finance.bankStatements.formatOption.${value}`, value),
              }))}
              hint={t(
                "finance.bankStatements.formatHint",
                "CAMT.053 and OFX describe themselves; CSV is only as trustworthy as its profile.",
              )}
            />
            <SelectField
              id="import-profile"
              label={t("finance.bankStatements.profile", "CSV dialect")}
              value={profileId}
              onValueChange={setProfileId}
              options={[
                {
                  value: NONE,
                  label: t("finance.bankStatements.profileDefault", "The account's active profile"),
                },
                ...(profilesQuery.data ?? []).map((profile) => ({
                  value: profile.id,
                  label: profile.name,
                })),
              ]}
              disabled={!canReadProfiles || format !== "csv" || importAccountId === NONE}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="import-file">
                {t("finance.bankStatements.file", "Statement file")}
              </Label>
              <Input
                id="import-file"
                type="file"
                ref={fileInputRef}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setFormError(null);
                }}
              />
            </div>

            {formError === null ? null : (
              <div className="sm:col-span-2 lg:col-span-4">
                <ProblemBanner>{formError}</ProblemBanner>
              </div>
            )}

            <div className="sm:col-span-2 lg:col-span-4">
              <Button
                type="button"
                loading={importMutation.isPending}
                onClick={() => void submitImport()}
              >
                {t("finance.bankStatements.import", "Import statement")}
              </Button>
            </div>

            {result === null ? null : (
              <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-4">
                {result.gap_detected ? (
                  <ProblemBanner>
                    {result.expected_opening_balance === null
                      ? t(
                          "finance.bankStatements.gapDetected",
                          "The opening balance does not continue this account's stored closing balance. Movements are missing.",
                        )
                      : t(
                          "finance.bankStatements.gapDetectedExpected",
                          "The opening balance does not continue this account's stored closing balance of {{expected}}. Movements are missing.",
                          { expected: result.expected_opening_balance.amount },
                        )}
                  </ProblemBanner>
                ) : (
                  <NoticeBanner>
                    {t(
                      "finance.bankStatements.importSummary",
                      "{{imported}} movements imported, {{duplicates}} already recorded.",
                      {
                        imported: result.imported_line_count,
                        duplicates: result.duplicate_line_count,
                      },
                    )}
                  </NoticeBanner>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="statement-bank-account-filter"
            label={t("finance.bankStatements.filterBankAccount", "Filter by account")}
            value={bankAccountFilter}
            onValueChange={setBankAccountFilter}
            className="w-72"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...bankAccounts.map((account) => ({
                value: account.id,
                label: `${account.code} · ${account.name}`,
              })),
            ]}
          />
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        query={listQuery}
        getRowId={(row) => row.id}
        onRowClick={
          onOpenStatementLines === undefined ? undefined : (row) => onOpenStatementLines(row.id)
        }
        storageKey="finance.bankStatements.columns"
        emptyMessage={t("finance.bankStatements.empty", "No statements imported yet.")}
      />
    </div>
  );
}
